/**
 * The web layer's half of an over-the-air update, inside the Capacitor shell.
 *
 * Who owns what is the whole design, and it is split where it is because of *when*
 * each half has to run:
 *
 *  - **The web layer downloads and verifies.** It has `fetch`, it has WebCrypto,
 *    and it is the half that can be updated without a Play release - so the logic
 *    that decides whether a release is good lives here, in `packages/web-update`,
 *    where it is testable on a workstation.
 *  - **The shell swaps and counts boots.** It has to: the bundle to serve is chosen
 *    before any JavaScript runs, and a bundle that fails to boot is exactly a
 *    bundle whose JavaScript never runs. So `activate`, `current`, `rollback` and
 *    `markBootAttempt` are native calls into `BundleStore.kt`.
 *
 * The bridge is reached through `window.Capacitor` rather than by importing
 * `@capacitor/core`. That is deliberate: the web app is also the plain browser app,
 * and importing a native shell's runtime would put it in every bundle - and in every
 * test - to serve a code path that only exists when the app is installed. The
 * interface below is the part of Capacitor's contract this code uses, which makes it
 * explicit, and makes a fake bridge in a test a five-line object.
 */

import {
  createUpdater,
  type UpdateStateListener,
  type Updater,
  type UpdateStore,
  type BootGuardStore,
} from '@unsorted/web-update';
import { toBase64 } from '../base64.js';

/** The global Capacitor injects into the webview, as much of it as this needs. */
export interface CapacitorGlobals {
  Capacitor?: {
    isNativePlatform?: () => boolean;
    Plugins?: Record<string, unknown>;
  };
}

export interface CapacitorBridge {
  isNative(): boolean;
  /** The plugin of that name, or undefined when the shell does not have it. */
  plugin<T>(name: string): T | undefined;
}

/** Only the Filesystem plugin calls this code makes. */
export interface FilesystemPlugin {
  writeFile(options: {
    path: string;
    data: string;
    encoding: string;
    directory: string;
    recursive?: boolean;
  }): Promise<unknown>;
  rmdir(options: { path: string; directory: string; recursive?: boolean }): Promise<unknown>;
  readFile(options: { path: string; directory: string; encoding?: string }): Promise<{ data: string }>;
  /**
   * Move a file, which is how the art index is promoted (`art-index.ts`).
   *
   * A rename rather than a copy on purpose: it is atomic within a directory, so the
   * file the app reads is either the one it had or the whole new one, and never a
   * half-written mixture.
   */
  rename(options: {
    from: string;
    to: string;
    directory?: string;
    toDirectory?: string;
  }): Promise<unknown>;
}

/** The shell's bundle plugin: the swap and the boot count. */
export interface WebBundlePlugin {
  current(): Promise<{ version: string; previous: string | null; attempts: number }>;
  activate(options: { version: string }): Promise<unknown>;
  markBootAttempt(): Promise<{ version: string; attempts: number }>;
  confirmBoot(): Promise<unknown>;
  rollback(): Promise<{ version: string }>;
}

/** The file an update wrote, as the filesystem call saw it. */
export interface DownloadedFile {
  data: string;
  encoding: string;
}

/** Capacitor's app-private directory. Not `DOCUMENTS`, which a user can browse. */
export const DATA_DIRECTORY = 'DATA';

/** Where a version is staged before the device promotes it. Mirrors `BundleStore.kt`. */
export const STAGING_DIRECTORY = 'staging';

export function capacitorBridge(
  target: CapacitorGlobals = globalThis as unknown as CapacitorGlobals,
): CapacitorBridge | null {
  const capacitor = target.Capacitor;
  if (capacitor?.isNativePlatform?.() !== true) return null;
  return {
    isNative: () => true,
    plugin: <T>(name: string): T | undefined => capacitor.Plugins?.[name] as T | undefined,
  };
}

/**
 * The store the updater writes into, backed by the shell's filesystem and plugin.
 *
 * Staging is a real directory per version, so an attempt that is interrupted can
 * never be found again as a candidate bundle, and `activate` is one call into
 * native code that promotes the whole directory atomically.
 */
export function createShellOtaStore(webBundle: WebBundlePlugin, files: FilesystemPlugin): UpdateStore {
  return {
    stage: async (version, path, bytes) => {
      await files.writeFile({
        path: `${STAGING_DIRECTORY}/${version}/${path}`,
        data: toBase64(bytes),
        encoding: 'base64',
        directory: DATA_DIRECTORY,
        recursive: true,
      });
    },
    discardStaged: async () => {
      try {
        await files.rmdir({ path: STAGING_DIRECTORY, directory: DATA_DIRECTORY, recursive: true });
      } catch {
        // Nothing staged is the normal case on a device that has never updated.
      }
    },
    activate: async (version) => {
      await webBundle.activate({ version });
    },
    running: async () => (await webBundle.current()).version,
  };
}

/** The boot count and the rollback, both of which the shell keeps. */
export function createShellBootGuard(webBundle: WebBundlePlugin): BootGuardStore {
  return {
    markBootAttempt: async () => (await webBundle.markBootAttempt()).attempts,
    confirmBoot: async () => {
      await webBundle.confirmBoot();
    },
    running: async () => (await webBundle.current()).version,
    rollback: async () => (await webBundle.rollback()).version,
  };
}

export interface ShellUpdaterOptions {
  bridge: CapacitorBridge | null;
  /** The shell's Filesystem plugin, or undefined to look it up on the bridge. */
  files?: FilesystemPlugin;
  /** The published manifest, e.g. `/api/updates/web/manifest.json` on the same origin. */
  manifestUrl: string;
  fetchImpl?: typeof fetch;
  onState?: UpdateStateListener;
}

/**
 * The updater, when this is a shell that can take one - and null when it is not.
 *
 * Null rather than an error for the two ordinary cases: a browser tab, where the
 * service worker's cache is the whole update story, and an installed app on an
 * older shell that predates the bundle plugin, where an update would have nowhere
 * to go. Both are "no update path available", which is a thing the app can be told
 * once instead of failing at every launch.
 */
export async function shellUpdater(options: ShellUpdaterOptions): Promise<Updater | null> {
  const { bridge } = options;
  if (bridge === null || !bridge.isNative()) return null;
  const webBundle = bridge.plugin<WebBundlePlugin>('WebBundle');
  const files = options.files ?? bridge.plugin<FilesystemPlugin>('Filesystem');
  if (webBundle === undefined || files === undefined) return null;

  return createUpdater({
    kind: 'web',
    manifestUrl: options.manifestUrl,
    store: createShellOtaStore(webBundle, files),
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
    ...(options.onState === undefined ? {} : { onState: options.onState }),
  });
}

/**
 * Tell the shell this bundle came up.
 *
 * Called once the app has rendered, which is the only honest definition of "up"
 * available from inside a bundle: the count is what turns a bundle that never gets
 * this far into one that is rolled back on the next launch.
 */
export async function confirmShellBoot(bridge: CapacitorBridge | null): Promise<void> {
  const webBundle = bridge?.plugin<WebBundlePlugin>('WebBundle');
  if (webBundle === undefined) return;
  try {
    await webBundle.confirmBoot();
  } catch (error) {
    // A confirmation that fails means the next launch counts again. Worse outcomes
    // exist, and crashing the app to report this one is not worth it.
    console.warn('the shell could not confirm this bundle booted', error);
  }
}
